import { useQueryClient } from '@tanstack/react-query'
import classNames from 'classnames'
import { useCallback, useState } from 'react'
import GridLayout from 'react-grid-layout'

import type { ReactElement } from 'react'
import type { Layout } from 'react-grid-layout'
import type { Menu } from '~/components/ActionMenu'
import type { IPostInCategoryPosition } from '~/types/catalog'

import CatalogPostCard from '~/components/CatalogPostCard'
import ModalDialog from '~/components/ModalDialog'
import MwMLoader from '~/components/MwMLoader'
import { AUTHORIZATIONS, TOAST_ERRORS } from '~/const'
import { useIsUserAuthorized } from '~/providers/Authorizations'
import {
  keys as catalogKeys,
  useCategoryPostsListQuery,
  useDeletePostFromCategoryMutation,
  useEnablePostInCategoryMutation,
  useUpdatePostsInCategoryPositionsMutation,
} from '~/queries/catalog'
import { handleGenericError } from '~/utils/error'
import { copyText } from '~/utils/navigator'
import { displayToast } from '~/utils/toast'

import './styles.scss'

const generatePosition = (
  i: string,
  idx: number,
  isCompactMode: boolean
): {
  h: number
  i: string
  w: number
  x: number
  y: number
} => ({
  h: 1,
  i,
  w: isCompactMode ? 1.5 : 3,
  x: isCompactMode ? (idx % 8) * 1.5 : (idx % 4) * 3,
  y: isCompactMode ? Math.floor(idx / 8) : Math.floor(idx / 4),
})

interface Props {
  categoryId?: string
  className?: string
  isCompactModeSelected: boolean
  onOpenAddToCategoryModal: (id: string) => void
  onOpenAddToDailyModal: (id: string) => void
}

const PostsPanel = ({
  categoryId,
  className,
  isCompactModeSelected,
  onOpenAddToCategoryModal,
  onOpenAddToDailyModal,
}: Props): ReactElement => {
  /** Local state */

  const isUserWriteAuthorized = useIsUserAuthorized([AUTHORIZATIONS.CCC.CATALOG.WRITE])

  const queryClient = useQueryClient()

  const componentClasses = classNames('posts-panel-component', className)

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  const [selectedPostId, setSelectedPostId] = useState('')

  /** Queries & Mutations */

  const { data: posts, isLoading: isPostsLoading } = useCategoryPostsListQuery(categoryId)

  const { mutateAsync: enablePostInCategory } = useEnablePostInCategoryMutation()

  const { mutateAsync: deletePostFromCategory } = useDeletePostFromCategoryMutation()

  const { mutateAsync: updatePostsInCategoryPositions } = useUpdatePostsInCategoryPositionsMutation()

  /** Handlers */

  const handleDeleteModalState = (isOpen: boolean) => () => setIsDeleteModalOpen(isOpen)

  const handleDeleteClick = useCallback(
    (postId: string) => () => {
      if (!isUserWriteAuthorized) {
        displayToast(TOAST_ERRORS.NOT_AUTHORIZED, 'error')
        return
      }

      setSelectedPostId(postId)
      setIsDeleteModalOpen(true)
    },
    [isUserWriteAuthorized]
  )

  const handleDeleteConfirm = (postId: string) => async () => {
    if (categoryId) {
      try {
        await deletePostFromCategory({ categoryName: categoryId, postId })
        await queryClient.invalidateQueries({ queryKey: catalogKeys.categoryPosts(categoryId) })
        setIsDeleteModalOpen(false)
        displayToast('Post successfully deleted from category', 'success')
      } catch (e) {
        handleGenericError(e)
      }
    }
  }

  const handleEnablePostInCategory = useCallback(
    async (postId: string, isEnabled: boolean) => {
      if (!isUserWriteAuthorized) {
        displayToast(TOAST_ERRORS.NOT_AUTHORIZED, 'error')
        return
      }

      if (categoryId) {
        try {
          await enablePostInCategory({ categoryName: categoryId, enabled: isEnabled, postId })
          await queryClient.invalidateQueries({ queryKey: catalogKeys.categoryPosts(categoryId) })
        } catch (e) {
          handleGenericError(e)
        }
      }
    },
    [categoryId, enablePostInCategory, isUserWriteAuthorized, queryClient]
  )

  const handleDragStop = async (layout: Layout[]) => {
    if (categoryId) {
      const formattedNewLayout: IPostInCategoryPosition[] = layout.map(({ i, x, y }) => {
        return {
          category_name: categoryId,
          position: isCompactModeSelected ? y * 8 + x / 1.5 : y * 4 + x / 3,
          post_id: i,
        }
      })

      try {
        await updatePostsInCategoryPositions({ positions: formattedNewLayout })
        await queryClient.invalidateQueries({ queryKey: catalogKeys.categoryPosts(categoryId) })
      } catch (e) {
        handleGenericError(e)
      }
    }
  }

  const handleCopyId = useCallback((coloringTemplateId: string) => copyText(coloringTemplateId), [])

  /** Render */

  const menu = useCallback(
    (enabled: boolean, coloringTemplateId: string): Menu => ({
      actions: [
        {
          action: () => handleEnablePostInCategory(coloringTemplateId, !enabled),
          label: enabled ? 'Disable' : 'Enable',
        },
        {
          action: handleDeleteClick(coloringTemplateId),
          label: 'Delete',
        },
        {
          action: () => onOpenAddToCategoryModal(coloringTemplateId),
          label: 'Add to category',
        },
        {
          action: () => onOpenAddToDailyModal(coloringTemplateId),
          label: 'Assign to a day',
        },
        {
          action: () => handleCopyId(coloringTemplateId),
          label: 'Copy the ID',
        },
      ],
    }),
    [handleCopyId, handleDeleteClick, handleEnablePostInCategory, onOpenAddToCategoryModal, onOpenAddToDailyModal]
  )

  return (
    <>
      <div className={componentClasses}>
        {isPostsLoading ? (
          <div className="posts-panel-component__no-post-wrapper">
            <MwMLoader category="item" />
          </div>
        ) : posts && posts?.length > 0 ? (
          <GridLayout
            className="posts-panel-component__layout"
            cols={12}
            compactType="horizontal"
            containerPadding={[0, 0]}
            isResizable={false}
            margin={[10, 10]}
            onDragStop={handleDragStop}
            rowHeight={isCompactModeSelected ? 126 : 262}
            width={1078}
          >
            {posts?.map(
              ({ cleaned_prompt, coloring_template_id, enabled, origin_type, preview_png_file_id }, index) => (
                <div
                  data-grid={generatePosition(coloring_template_id, index, isCompactModeSelected)}
                  key={coloring_template_id}
                >
                  <CatalogPostCard
                    actionsMenu={menu(enabled, coloring_template_id)}
                    id={coloring_template_id}
                    isDisabled={!enabled}
                    mediaUrl={preview_png_file_id}
                    mode={isCompactModeSelected ? 'compact' : 'detailed'}
                    prompt={cleaned_prompt}
                    templateOrigin={origin_type}
                  />
                </div>
              )
            )}
          </GridLayout>
        ) : (
          <div className="posts-panel-component__no-post-wrapper">
            {categoryId ? 'No posts found in this category' : 'Select a category'}
          </div>
        )}
      </div>
      <ModalDialog
        isOpen={isDeleteModalOpen}
        mainText="Are you sure you want to delete this post from this category?"
        onClose={handleDeleteModalState(false)}
        onConfirm={handleDeleteConfirm(selectedPostId)}
      />
    </>
  )
}

export default PostsPanel
